// client/src/api.js v3 — JWT-authenticated API layer
const BASE = '';

const getToken = () => localStorage.getItem('ipe_token');

const req = async (method, path, body) => {
  const headers = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${BASE}${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
  if (res.status === 401) {
    localStorage.removeItem('ipe_token');
    localStorage.removeItem('ipe_user');
    window.location.reload();
    return;
  }
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
};

// Generic helpers for ad-hoc calls (used by BulkClassify etc.)
export const api = {
  get:   (path)       => req('GET',   path),
  patch: (path, body) => req('PATCH', path, body),
  post:  (path, body) => req('POST',  path, body),
  // Auth
  login:          (email, password) => req('POST', '/api/auth/login', { email, password }),
  me:             ()                => req('GET',  '/api/auth/me'),
  changePassword: (current, next)   => req('POST', '/api/auth/change-password', { current_password: current, new_password: next }),
  getUsers:       ()                => req('GET',  '/api/auth/users'),
  createUser:     (data)            => req('POST', '/api/auth/users', data),
  updateUser:     (id, data)        => req('PATCH', `/api/auth/users/${id}`, data),
  resetPassword:  (id)              => req('POST', `/api/auth/reset-password/${id}`),

  // Tenders
  getTenders:     ()                => req('GET',  '/api/tenders'),
  getTender:      (id)              => req('GET',  `/api/tenders/${id}`),
  createTender:   (data)            => req('POST', '/api/tenders', data),
  updateTender:   (id, data)        => req('PATCH', `/api/tenders/${id}`, data),
  deleteTender:   (id)              => req('DELETE', `/api/tenders/${id}`),
  archiveTender:  (id)              => req('POST',   `/api/tenders/${id}/archive`),
  bidDecision:    (id, data)        => req('POST', `/api/tenders/${id}/bid-decision`, data),
  getContractReview:    (id)       => req('GET',  `/api/tenders/${id}/contract-review`),
  saveContractReview:   (id, data) => req('POST', `/api/tenders/${id}/contract-review`, data),
  rejectContractReview:  (id, data) => req('POST', `/api/tenders/${id}/contract-review/reject`, data),
  abandonContractReview: (id, data) => req('POST', `/api/tenders/${id}/contract-review/abandon`, data),

  // Reference data
  getIDNOs:            ()   => req('GET',  '/api/reference/idnos'),
  createIDNO:          (n)  => req('POST', '/api/reference/idnos', { name: n }),
  getClassifications:  ()   => req('GET',  '/api/reference/classifications'),
  createClassification:(n)  => req('POST', '/api/reference/classifications', { name: n }),
  getEstimators:       ()   => req('GET',  '/api/reference/estimators'),
  getEstimatorUsers:   ()   => req('GET',  '/api/tenders/estimator-users'),
  createEstimator:     (d)  => req('POST', '/api/reference/estimators', d),
  updateEstimator:     (id,d)=> req('PATCH',`/api/reference/estimators/${id}`, d),
  getClassificationTargets:    ()  => req('GET',  '/api/admin/classification-targets'),
  updateClassificationTargets: (targets) => req('PATCH', '/api/admin/classification-targets', { targets }),

  // Analytics
  getSummary:    (months) => req('GET', `/api/analytics/summary${months ? `?months=${months}` : ''}`),
  getCommercial: (months) => req('GET', `/api/analytics/commercial${months ? `?months=${months}` : ''}`),
  getWorkload:   (months) => req('GET', `/api/analytics/workload${months ? `?months=${months}` : ''}`),
  getTrends:     (months) => req('GET', `/api/analytics/trends${months ? `?months=${months}` : ''}`),

  // Export
  exportCSV:  (params) => `${BASE}/api/export/csv?${new URLSearchParams(params)}`,
  exportXLSX: (params) => `${BASE}/api/export/xlsx?${new URLSearchParams(params)}`,

  // Email
  testEmail: () => req('GET', '/api/email/test'),

  // CRM — Leads
  crmLeads: (params={}) => { const q=new URLSearchParams(params).toString(); return req('GET','/api/crm/leads'+(q?'?'+q:'')); },
  crmLead: (id) => req('GET','/api/crm/leads/'+id),
  crmCreateLead: (data) => req('POST','/api/crm/leads',data),
  crmUpdateLead: (id,data) => req('PATCH','/api/crm/leads/'+id,data),
  crmAdvanceLead: (id) => req('POST','/api/crm/leads/'+id+'/advance'),
  crmMoveLead: (id, targetStage) => req('POST','/api/crm/leads/'+id+'/move', { target_stage: targetStage }),
  crmLeadDecision: (id,data) => req('POST','/api/crm/leads/'+id+'/decision',data),
  crmPipeline: () => req('GET','/api/crm/pipeline'),
  // CRM — Inbox
  crmInbox: () => req('GET','/api/crm/inbox'),
  crmLogInbox: (data) => req('POST','/api/crm/inbox',data),
  crmLostEscalations: () => req('GET','/api/crm/escalations/lost-feedback'),
  crmCreateInbox: (data) => req('POST','/api/crm/inbox',data),
  crmPromoteInbox: (id,data) => req('POST','/api/crm/inbox/'+id+'/promote',data),
  crmDiscardInbox: (id) => req('POST','/api/crm/inbox/'+id+'/discard'),
  // CRM — Interactions
  crmInteractions: (params={}) => { const q=new URLSearchParams(params).toString(); return req('GET','/api/crm/interactions'+(q?'?'+q:'')); },
  crmLogInteraction: (id,data) => req('POST','/api/crm/leads/'+id+'/interactions',data),
  crmCreateInteraction: (data) => req('POST','/api/crm/interactions',data),
  // CRM — Companies
  crmCompanies: (search) => req('GET','/api/crm/companies'+(search?'?search='+encodeURIComponent(search):'')),
  crmCreateCompany: (data) => req('POST','/api/crm/companies',data),
  crmUpdateCompany: (id,data) => req('PATCH','/api/crm/companies/'+id,data),
  // CRM — Contacts
  crmContacts: (params={}) => { const q=new URLSearchParams(params).toString(); return req('GET','/api/crm/contacts'+(q?'?'+q:'')); },
  crmContact: (id) => req('GET','/api/crm/contacts/'+id),
  crmCreateContact: (data) => req('POST','/api/crm/contacts',data),
  crmAddIntel: (id,data) => req('POST','/api/crm/contacts/'+id+'/intel',data),
  crmTransferContact: (id,data) => req('PATCH','/api/crm/contacts/'+id+'/transfer',data),
  crmUpdateContact: (id,data) => req('PATCH','/api/crm/contacts/'+id, data),
  crmDeleteContact: (id) => req('DELETE','/api/crm/contacts/'+id),
  // CRM — Reference data
  crmClassifications: () => req('GET','/api/crm/classifications'),
  crmIdnos: () => req('GET','/api/crm/idnos'),
  crmArchiveLead: (id) => req('POST','/api/crm/leads/'+id+'/archive'),
  crmRestoreLead: (id) => req('POST','/api/crm/leads/'+id+'/restore'),
  crmLeadAudit: (id) => req('GET','/api/crm/leads/'+id+'/audit-log'),
};
